import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "../sanity/client";
import { PortableText } from "@portabletext/react";
import useIntersectionObserver from "../hooks/useIntersectionObserver";
import {
	toTitleCase,
	isPastEvent,
	formatLongDate,
	formatTime,
	formatEventType,
	getTicketSummary,
	getMapUrl,
	buildCalendarUrl,
} from "./subComponents/eventUtils";

const EVENT_QUERY = `*[_type == "event" && _id == $id][0]{
	_id,
	name,
	status,
	date,
	endDate,
	timezone,
	doorsOpen,
	eventType,
	virtualEventLink,
	venue->{ name, address, googleMapsLocation },
	speakers[]->{ name, title, company, "image": profileImage.asset->url },
	description,
	agenda[]{ time, title, description, speaker->{ name } },
	capacity,
	registrationRequired,
	registrationDeadline,
	ticketInformation[]{ name, price, currency, availability, purchaseLink },
	sponsors[]{ name, website },
	faq[]{ question, answer },
	contactInfo,
	socialLinks,
	"image": image.asset->url
}`;

const SOCIAL_ICONS = {
	facebook: "fa-facebook",
	twitter: "fa-x-twitter",
	linkedin: "fa-linkedin",
	instagram: "fa-instagram",
};

const EventDetail = () => {
	const { id } = useParams();
	const [event, setEvent] = useState(null);
	const [loading, setLoading] = useState(true);

	const sectionsRef = useIntersectionObserver(
		(target) => target.classList.add("visible"),
		{ threshold: 0.1 },
	);

	useEffect(() => {
		const fetchEvent = async () => {
			try {
				const data = await client.fetch(EVENT_QUERY, { id });
				setEvent(data);
			} catch (error) {
				console.error("Error fetching event:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchEvent();
	}, [id]);

	if (loading) {
		return (
			<div className="loadingAnimationContainer">
				<l-ring size="40" stroke="5" bg-opacity="0" speed="2" color="black"></l-ring>
			</div>
		);
	}

	if (!event) {
		return (
			<div className="eventDetailsSection">
				<div className="eventDetailNotFound">
					<h2 className="eventDetailNotFoundTitle">Event not found</h2>
					<p className="eventDetailNotFoundText">
						This event may have been removed or the link is out of date.
					</p>
					<Link to="/events" className="btn btnGhostDark">
						Back to all events
					</Link>
				</div>
			</div>
		);
	}

	const isPast = isPastEvent(event.date);
	const formattedEventType = formatEventType(event.eventType);
	const ticketSummary = getTicketSummary(event.ticketInformation);
	const ticketLink = event.ticketInformation?.find(
		(ticket) => ticket.purchaseLink,
	)?.purchaseLink;
	const mapUrl = getMapUrl(event.venue);
	const calendarUrl = buildCalendarUrl(event);
	const hasSocialLinks =
		event.socialLinks &&
		Object.entries(SOCIAL_ICONS).some(([key]) => event.socialLinks[key]);

	const facts = [];
	if (event.doorsOpen) {
		facts.push({
			icon: "fa-door-open",
			text: `Doors open ${event.doorsOpen} min before`,
		});
	}
	if (event.venue?.name) {
		facts.push({ icon: "fa-location-dot", text: event.venue.name });
	} else if (formattedEventType) {
		facts.push({ icon: "fa-video", text: formattedEventType });
	}
	if (event.virtualEventLink) {
		facts.push({
			icon: "fa-video",
			text: "Join link",
			href: event.virtualEventLink,
			external: true,
		});
	}
	if (ticketSummary) {
		facts.push({ icon: "fa-ticket", text: ticketSummary });
	}
	if (event.capacity) {
		facts.push({ icon: "fa-users", text: `${event.capacity} people` });
	}
	if (event.registrationRequired) {
		facts.push({
			icon: "fa-clipboard-check",
			text: `Registration required${
				event.registrationDeadline
					? ` by ${formatLongDate(event.registrationDeadline)}`
					: ""
			}`,
		});
	}
	if (event.contactInfo?.email) {
		facts.push({
			icon: "fa-envelope",
			text: event.contactInfo.email,
			href: `mailto:${event.contactInfo.email}`,
		});
	}
	if (event.contactInfo?.phone) {
		facts.push({
			icon: "fa-phone",
			text: event.contactInfo.phone,
			href: `tel:${event.contactInfo.phone}`,
		});
	}

	return (
		<div className="eventDetailsSection">
			<div className="eventDetailBackRow">
				<Link to="/events" className="eventDetailBackLink">
					← All events
				</Link>
			</div>

			<div className="eventDetailBody">
				<div
					ref={(el) => (sectionsRef.current[0] = el)}
					className="eventDetailCard animate"
				>
					{event.image && (
						<div className="eventDetailCardImageCol">
							<div className="eventDetailCardImageMat">
								<img
									className="eventDetailCardImage"
									src={event.image}
									alt={event.name}
								/>
							</div>
						</div>
					)}

					<div className="eventDetailCardBody">
						<span
							className={`eventDetailStatus ${isPast ? "past" : "upcoming"}`}
						>
							{isPast ? "Past event" : "Upcoming"}
						</span>

						<p className="eventDetailCardDate">{formatLongDate(event.date)}</p>
						<p className="eventDetailCardTime">
							{formatTime(event.date)} {event.timezone || "CST"}
						</p>

						<h1 className="eventDetailCardName">
							{toTitleCase(event.name)}
						</h1>

						{facts.length > 0 && (
							<div className="eventDetailCardFacts">
								{facts.map((fact, index) => (
									<div className="eventDetailCardFact" key={index}>
										<i className={`fa-solid ${fact.icon}`} aria-hidden="true"></i>
										{fact.href ? (
											<a
												href={fact.href}
												target={fact.external ? "_blank" : undefined}
												rel={fact.external ? "noopener noreferrer" : undefined}
											>
												{fact.text}
											</a>
										) : (
											<span>{fact.text}</span>
										)}
									</div>
								))}
							</div>
						)}

						<div className="eventDetailCardActions">
							{ticketLink && (
								<a
									href={ticketLink}
									target="_blank"
									rel="noopener noreferrer"
									className="btn btnPrimary"
								>
									Get tickets
									<span className="btnIcon" aria-hidden="true">
										<i className="fa-solid fa-arrow-up-right"></i>
									</span>
								</a>
							)}

							{mapUrl && (
								<a
									href={mapUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="btn btnGhostDark"
								>
									View map
									<span className="btnIcon" aria-hidden="true">
										<i className="fa-solid fa-location-dot"></i>
									</span>
								</a>
							)}

							{calendarUrl && (
								<a
									href={calendarUrl}
									download={`${toTitleCase(event.name)}.ics`}
									className="btn btnGhostDark"
								>
									Add to calendar
									<span className="btnIcon" aria-hidden="true">
										<i className="fa-solid fa-calendar-plus"></i>
									</span>
								</a>
							)}
						</div>

						{hasSocialLinks && (
							<div className="eventDetailCardSocial">
								{Object.entries(SOCIAL_ICONS).map(
									([key, iconClass]) =>
										event.socialLinks[key] && (
											<a
												key={key}
												href={event.socialLinks[key]}
												target="_blank"
												rel="noopener noreferrer"
												aria-label={`Event on ${key}`}
											>
												<i className={`fa-brands ${iconClass}`}></i>
											</a>
										),
								)}
							</div>
						)}
					</div>
				</div>

				<div
					ref={(el) => (sectionsRef.current[1] = el)}
					className="eventDetailMain animate"
				>
					{event.description && (
						<div className="eventDetailDescription">
							<PortableText value={event.description} />
						</div>
					)}

					{event.agenda?.length > 0 && (
						<section className="eventDetailBlock">
							<h2 className="eventDetailBlockHeading">Agenda</h2>
							<ol className="eventDetailAgenda">
								{event.agenda.map((item, index) => (
									<li key={index} className="eventDetailAgendaItem">
										<span className="eventDetailAgendaTime">{item.time}</span>
										<div>
											<p className="eventDetailAgendaTitle">{item.title}</p>
											{item.description && (
												<p className="eventDetailAgendaDesc">
													{item.description}
												</p>
											)}
											{item.speaker?.name && (
												<p className="eventDetailAgendaSpeaker">
													{item.speaker.name}
												</p>
											)}
										</div>
									</li>
								))}
							</ol>
						</section>
					)}

					{event.speakers?.length > 0 && (
						<section className="eventDetailBlock">
							<h2 className="eventDetailBlockHeading">Speakers</h2>
							<div className="eventDetailSpeakers">
								{event.speakers.map((speaker, index) => (
									<div className="eventDetailSpeaker" key={index}>
										{speaker.image && (
											<img
												src={speaker.image}
												alt={speaker.name}
												className="eventDetailSpeakerPhoto"
											/>
										)}
										<div>
											<p className="eventDetailSpeakerName">{speaker.name}</p>
											{(speaker.title || speaker.company) && (
												<p className="eventDetailSpeakerRole">
													{[speaker.title, speaker.company]
														.filter(Boolean)
														.join(" · ")}
												</p>
											)}
										</div>
									</div>
								))}
							</div>
						</section>
					)}

					{event.faq?.length > 0 && (
						<section className="eventDetailBlock">
							<h2 className="eventDetailBlockHeading">Questions</h2>
							{event.faq.map((item, index) => (
								<div className="eventDetailFaqItem" key={index}>
									<p className="eventDetailFaqQuestion">{item.question}</p>
									{item.answer && (
										<div className="eventDetailFaqAnswer">
											<PortableText value={item.answer} />
										</div>
									)}
								</div>
							))}
						</section>
					)}

					{event.sponsors?.length > 0 && (
						<p className="eventDetailSponsors">
							With support from{" "}
							{event.sponsors.map((sponsor, index) => (
								<span key={index}>
									{sponsor.website ? (
										<a
											href={sponsor.website}
											target="_blank"
											rel="noopener noreferrer"
										>
											{sponsor.name.trim()}
										</a>
									) : (
										sponsor.name.trim()
									)}
									{index < event.sponsors.length - 1
										? index === event.sponsors.length - 2
											? " and "
											: ", "
										: ""}
								</span>
							))}
							.
						</p>
					)}
				</div>
			</div>
		</div>
	);
};

export default EventDetail;
